import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import ColorPicker from '../components/ColorPicker';
import { EVENT_SUBTHEMES, EVENT_CATEGORIES, REFERENCE_LABEL, REFERENCE_PLACEHOLDER, isGrouped } from '../constants';

function renderSubthemeOptions(subthemes) {
  if (!subthemes) return null;
  if (isGrouped(subthemes)) {
    return subthemes.groups.map(g => (
      <optgroup key={g.label} label={g.label}>
        {g.items.map(s => <option key={s} value={s}>{s}</option>)}
      </optgroup>
    ));
  }
  return subthemes.map(s => <option key={s} value={s}>{s}</option>);
}

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = startOffset - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), other: true });
  for (let i = 1; i <= lastDay.getDate(); i++) days.push({ date: new Date(year, month, i), other: false });
  while (days.length < 42) { const n = days.length - startOffset - lastDay.getDate() + 1; days.push({ date: new Date(year, month + 1, n), other: true }); }
  return days;
}

function emptyForm() {
  return { title: '', category: 'autre', subtheme: '', reference: '', child_id: '', start_time: '', end_time: '', color: '' };
}

// Convertit une date en format YYYY-MM-DD local
function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const CATEGORY_COLORS = {
  travail: '#3b82f6', maison: '#f97316', enfants: '#ec4899',
  administratif: '#8b5cf6', perso: '#14b8a6',
};

// Palette d'accents pour le calendrier
const CALENDAR_ACCENTS = [
  { value: '', label: 'Défaut (thème)', color: null },
  { value: '#a78bfa', label: 'Lavande', color: '#a78bfa' },
  { value: '#f9a8d4', label: 'Rose', color: '#f9a8d4' },
  { value: '#87a878', label: 'Sauge', color: '#87a878' },
  { value: '#38bdf8', label: 'Azur', color: '#38bdf8' },
  { value: '#fb923c', label: 'Corail', color: '#fb923c' },
  { value: '#eab308', label: 'Or', color: '#eab308' },
  { value: '#cb3040', label: 'Vermillon', color: '#cb3040' },
  { value: '#14b8a6', label: 'Jade', color: '#14b8a6' },
];

// Palette de couleurs de fond pour le calendrier
const CALENDAR_BACKGROUNDS = [
  { value: '', label: 'Défaut (thème)', bg: null },
  { value: 'ivoire', label: 'Ivoire', bg: '#fffbea' },
  { value: 'lavande', label: 'Lavande', bg: '#f5f3ff' },
  { value: 'rose', label: 'Rose poudré', bg: '#fdf2f8' },
  { value: 'menthe', label: 'Menthe', bg: '#f0fdf4' },
  { value: 'peche', label: 'Pêche', bg: '#fff7ed' },
  { value: 'ciel', label: 'Ciel', bg: '#eff6ff' },
  { value: 'sable', label: 'Sable', bg: '#fef3c7' },
  { value: 'perle', label: 'Gris perle', bg: '#f1f5f9' },
  { value: 'vieuxrose', label: 'Vieux rose', bg: '#fecdd3' },
  { value: 'sauge', label: 'Sauge', bg: '#ecfccb' },
];

export default function CalendarPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [childActivities, setChildActivities] = useState([]);
  const [children, setChildren] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [filterCat, setFilterCat] = useState('');

  // Toggles d'affichage des couches
  const [showEvents, setShowEvents] = useState(() => localStorage.getItem('cal_show_events') !== 'false');
  const [showTasks, setShowTasks] = useState(() => localStorage.getItem('cal_show_tasks') !== 'false');
  const [showChildActivities, setShowChildActivities] = useState(() => localStorage.getItem('cal_show_child') !== 'false');

  // Couleur d'accent + couleur de fond du calendrier — stockées par thème
  const getAccentKey = () => `cal_accent_${localStorage.getItem('theme') || 'clair'}`;
  const getBgKey = () => `cal_bg_${localStorage.getItem('theme') || 'clair'}`;
  const [accent, setAccent] = useState(() => localStorage.getItem(getAccentKey()) || '');
  const [calBg, setCalBg] = useState(() => localStorage.getItem(getBgKey()) || '');

  // Re-lit les couleurs si le thème change
  useEffect(() => {
    const sync = () => {
      setAccent(localStorage.getItem(getAccentKey()) || '');
      setCalBg(localStorage.getItem(getBgKey()) || '');
    };
    window.addEventListener('storage', sync);
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => { window.removeEventListener('storage', sync); observer.disconnect(); };
  }, []);

  useEffect(() => { localStorage.setItem('cal_show_events', showEvents); }, [showEvents]);
  useEffect(() => { localStorage.setItem('cal_show_tasks', showTasks); }, [showTasks]);
  useEffect(() => { localStorage.setItem('cal_show_child', showChildActivities); }, [showChildActivities]);
  useEffect(() => { localStorage.setItem(getAccentKey(), accent); }, [accent]);
  useEffect(() => { localStorage.setItem(getBgKey(), calBg); }, [calBg]);

  const selectedBg = CALENDAR_BACKGROUNDS.find(c => c.value === calBg) || CALENDAR_BACKGROUNDS[0];

  const loadAll = useCallback(async () => {
    try {
      const [evRes, tkRes, chRes] = await Promise.all([
        api.get('/events', { params: { month: month + 1, year } }),
        api.get('/tasks'),
        api.get('/children'),
      ]);
      setEvents(evRes.data);
      setTasks(tkRes.data);
      setChildren(chRes.data);

      // Charger les activités de tous les enfants
      const allActs = [];
      for (const child of chRes.data) {
        const r = await api.get(`/children/${child.id}/activities`);
        r.data.forEach(a => allActs.push({ ...a, child_name: child.name, child_color: child.color }));
      }
      setChildActivities(allActs);
    } catch {}
  }, [month, year]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const days = getCalendarDays(year, month);
  const todayStr = toLocalDateStr(today);

  const prev = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  const openAdd = (date) => {
    setSelectedDate(toLocalDateStr(date));
    setForm(emptyForm());
    setShowModal(true);
  };

  const addEvent = async (e) => {
    e.preventDefault();
    const catColor = EVENT_CATEGORIES.find(c => c.value === form.category)?.color || '#6366f1';
    const payload = {
      ...form,
      event_date: selectedDate,
      color: form.color || catColor,
      child_id: form.child_id || null,
      subtheme: form.subtheme || null,
      reference: form.reference || null,
    };
    await api.post('/events', payload);
    setShowModal(false);
    loadAll();
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    await api.delete(`/events/${id}`);
    loadAll();
  };

  // Récupérer les items d'une date donnée, avec type différencié
  const getItemsForDate = (date) => {
    const ds = toLocalDateStr(date);
    const items = [];

    // Événements (RDV réels)
    if (showEvents) {
      events.forEach(ev => {
        if (ev.event_date === ds && (!filterCat || ev.category === filterCat)) {
          items.push({ ...ev, __type: 'event' });
        }
      });
    }

    // Tâches
    if (showTasks) {
      tasks.forEach(t => {
        if (t.due_date === ds) {
          const color = t.color || CATEGORY_COLORS[t.category] || '#64748b';
          items.push({
            ...t, __type: 'task',
            start_time: t.due_time,
            displayColor: color,
            displayLabel: t.category === 'travail' && t.reference ? t.reference : t.title,
          });
        }
      });
    }

    // Activités enfants
    if (showChildActivities) {
      childActivities.forEach(a => {
        if (a.activity_date === ds) {
          items.push({
            ...a, __type: 'child',
            displayColor: a.child_color || '#ec4899',
            displayLabel: a.title,
          });
        }
      });
    }

    // Tri par heure puis type (événements d'abord)
    items.sort((a, b) => {
      if (a.start_time && b.start_time) return a.start_time.localeCompare(b.start_time);
      if (a.start_time) return -1;
      if (b.start_time) return 1;
      const typeOrder = { event: 0, task: 1, child: 2 };
      return typeOrder[a.__type] - typeOrder[b.__type];
    });

    return items;
  };

  // Style d'un item selon son type
  const renderItemInCell = (item) => {
    const color = item.__type === 'event' ? item.color : item.displayColor;
    const time = item.start_time ? item.start_time.slice(0, 5) + ' ' : '';
    const label = item.__type === 'event' ? item.title : item.displayLabel;
    const tooltip = [
      item.__type === 'event' ? '📅 RDV' : item.__type === 'task' ? '☐ Tâche' : '👶 Enfant',
      label,
      item.start_time && item.start_time.slice(0, 5),
      item.reference, item.child_name
    ].filter(Boolean).join(' · ');

    if (item.__type === 'event') {
      return (
        <div
          key={'ev-' + item.id}
          className="calendar-event"
          style={{ background: color }}
          title={tooltip}
        >
          {time}{label}
        </div>
      );
    }

    // Tâche ou activité enfant = style très transparent (presque filigrane)
    const prefix = item.__type === 'child' ? '👶 ' : '☐ ';
    return (
      <div
        key={item.__type + '-' + item.id}
        className="calendar-event"
        style={{
          background: color + '0a', // 0a en hex = ~4% opacity
          color: color,
          border: `1px dashed ${color}66`, // 66 = ~40% opacity sur la bordure
          fontStyle: 'italic',
          fontWeight: 400,
          opacity: 0.55,
        }}
        title={tooltip}
      >
        {prefix}{time}{label}
      </div>
    );
  };

  // Liste récapitulative en dessous
  const monthItems = React.useMemo(() => {
    const allItems = [];
    days.forEach(({ date, other }) => {
      if (other) return;
      getItemsForDate(date).forEach(i => allItems.push({ ...i, _date: toLocalDateStr(date) }));
    });
    return allItems.sort((a, b) => {
      if (a._date !== b._date) return a._date.localeCompare(b._date);
      if (a.start_time && b.start_time) return a.start_time.localeCompare(b.start_time);
      if (a.start_time) return -1;
      if (b.start_time) return 1;
      return 0;
    });
  }, [days, events, tasks, childActivities, showEvents, showTasks, showChildActivities, filterCat]); // eslint-disable-line

  return (
    <div>
      <div className="page-header"><h1>Calendrier</h1></div>

      {/* Filtres par catégorie d'événement */}
      <div className="task-filters">
        <button className={`filter-btn ${filterCat === '' ? 'active' : ''}`} onClick={() => setFilterCat('')}>Toutes catégories RDV</button>
        {EVENT_CATEGORIES.map(c => (
          <button
            key={c.value}
            className={`filter-btn ${filterCat === c.value ? 'active' : ''}`}
            onClick={() => setFilterCat(filterCat === c.value ? '' : c.value)}
            style={filterCat === c.value ? { background: c.color, borderColor: c.color } : { borderLeft: `4px solid ${c.color}` }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Sélecteurs de personnalisation du calendrier */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-light)', minWidth: 150 }}>🖼️ Fond du calendrier :</span>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {CALENDAR_BACKGROUNDS.map(b => (
              <button
                key={b.value || 'default-bg'}
                onClick={() => setCalBg(b.value)}
                title={b.label}
                style={{
                  width: 26, height: 26, borderRadius: 6,
                  background: b.bg || 'transparent',
                  border: calBg === b.value ? '2px solid var(--text)' : '2px solid var(--border)',
                  cursor: 'pointer',
                  padding: 0,
                  transform: calBg === b.value ? 'scale(1.1)' : 'scale(1)',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)',
                }}
              >
                {!b.bg && '∅'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-light)', minWidth: 150 }}>🎨 Accent :</span>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {CALENDAR_ACCENTS.map(a => (
              <button
                key={a.value || 'default'}
                onClick={() => setAccent(a.value)}
                title={a.label}
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: a.color || 'transparent',
                  border: accent === a.value ? '2px solid var(--text)' : '2px solid var(--border)',
                  boxShadow: accent === a.value ? '0 0 0 2px var(--bg-card)' : 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transform: accent === a.value ? 'scale(1.15)' : 'scale(1)',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)',
                }}
              >
                {!a.color && '∅'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Toggles des couches d'affichage */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap', background: 'var(--bg)', padding: 10, borderRadius: 10, border: '1px solid var(--border)' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-light)', alignSelf: 'center' }}>Afficher :</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
          <input type="checkbox" checked={showEvents} onChange={e => setShowEvents(e.target.checked)} />
          📅 RDV réels
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
          <input type="checkbox" checked={showTasks} onChange={e => setShowTasks(e.target.checked)} />
          <span style={{ fontStyle: 'italic' }}>☐ Tâches</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
          <input type="checkbox" checked={showChildActivities} onChange={e => setShowChildActivities(e.target.checked)} />
          <span style={{ fontStyle: 'italic' }}>👶 Activités enfants</span>
        </label>
      </div>

      <div className="card" style={selectedBg.bg ? { background: selectedBg.bg } : {}}>
        <div className="calendar-nav">
          <button className="btn-icon" onClick={prev}>←</button>
          <h2 style={accent ? { color: accent } : {}}>{MONTHS[month]} {year}</h2>
          <button className="btn-icon" onClick={next}>→</button>
          <button
            className="btn btn-primary btn-sm"
            style={accent ? { marginLeft: 'auto', background: accent, borderColor: accent } : { marginLeft: 'auto' }}
            onClick={() => openAdd(today)}
          >+ RDV</button>
        </div>
        <div className="calendar-grid">
          {DAYS.map(d => (
            <div key={d} className="calendar-header-cell" style={accent ? { color: accent } : {}}>{d}</div>
          ))}
          {days.map(({ date, other }, i) => {
            const ds = toLocalDateStr(date);
            const items = getItemsForDate(date);
            const isToday = ds === todayStr;
            const cellStyle = isToday && accent
              ? { borderColor: accent, background: accent + '15' }
              : {};
            return (
              <div
                key={i}
                className={`calendar-cell ${isToday ? 'today' : ''} ${other ? 'other-month' : ''} ${items.length ? 'has-event' : ''}`}
                style={cellStyle}
                onClick={() => !other && openAdd(date)}
              >
                <div className="calendar-day-num" style={isToday && accent ? { color: accent } : {}}>
                  {date.getDate()}
                </div>
                {items.slice(0, 4).map(item => renderItemInCell(item))}
                {items.length > 4 && <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 600 }}>+{items.length - 4}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste récapitulative */}
      {monthItems.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div className="day-section-title"><span className="section-emoji">📅</span> Tout ce qui est prévu en {MONTHS[month]}</div>
          {monthItems.map((item, idx) => {
            const color = item.__type === 'event' ? item.color : item.displayColor;
            const label = item.__type === 'event' ? item.title : item.displayLabel;
            const isReal = item.__type === 'event';
            return (
              <div key={item.__type + '-' + item.id + '-' + idx} className="timeline-item" style={!isReal ? { opacity: 0.5 } : {}}>
                <div className="timeline-time">
                  {new Date(item._date + 'T00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                  {item.start_time && <div style={{ fontSize: '0.7rem' }}>{item.start_time.slice(0, 5)}</div>}
                </div>
                <div className="timeline-dot" style={{ background: color, border: !isReal ? `2px dashed ${color}` : 'none', backgroundColor: !isReal ? 'white' : color }} />
                <div className="timeline-content">
                  <div className="timeline-title" style={!isReal ? { fontStyle: 'italic', fontWeight: 500 } : {}}>
                    {item.__type === 'event' ? '📅 ' : item.__type === 'task' ? '☐ ' : '👶 '}
                    {label}
                  </div>
                  <div className="timeline-sub">
                    {item.__type === 'event' && (
                      <>
                        <span className={`badge badge-${item.category}`}>{EVENT_CATEGORIES.find(c => c.value === item.category)?.label}</span>
                        {item.subtheme && ' · ' + item.subtheme}
                        {item.child_name && ' · 👶 ' + item.child_name}
                        {item.reference && ' · 📁 ' + item.reference}
                      </>
                    )}
                    {item.__type === 'task' && (
                      <>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Tâche</span>
                        {item.category && ' · ' + item.category}
                        {item.subtheme && ' · ' + item.subtheme}
                      </>
                    )}
                    {item.__type === 'child' && (
                      <>
                        <span style={{ fontSize: '0.75rem', color: item.child_color, fontWeight: 600 }}>👶 {item.child_name}</span>
                        {item.category && ' · ' + item.category}
                      </>
                    )}
                  </div>
                </div>
                {item.__type === 'event' && <button className="btn-ghost" onClick={() => deleteEvent(item.id)}>🗑️</button>}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" /* Pas de fermeture au clic extérieur */>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>📅 Nouveau RDV — {new Date(selectedDate + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
              <button type="button" className="btn-icon" title="Fermer" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={addEvent}>
              <div className="form-group">
                <label>Titre / Description</label>
                <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex : Audience TGI, Rentrée scolaire..." required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value, subtheme: '', reference: '', child_id: '' })}>
                    {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sous-thème</label>
                  <select className="form-input" value={form.subtheme} onChange={e => setForm({ ...form, subtheme: e.target.value })}>
                    <option value="">—</option>
                    {renderSubthemeOptions(EVENT_SUBTHEMES[form.category])}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{REFERENCE_LABEL[form.category] || 'Référence (optionnel)'}</label>
                {form.category === 'enfants' ? (
                  <select className="form-input" value={form.child_id} onChange={e => setForm({ ...form, child_id: e.target.value })}>
                    <option value="">— Choisir un enfant —</option>
                    {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input className="form-input" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder={REFERENCE_PLACEHOLDER[form.category] || ''} />
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Début</label>
                  <input className="form-input" type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Fin</label>
                  <input className="form-input" type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} />
                </div>
              </div>

              <ColorPicker value={form.color} onChange={c => setForm({ ...form, color: c })} label="Couleur (sinon celle de la catégorie)" />

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
